import React, { useState, useEffect } from 'react'
import { UsersIcon, PlusIcon, CogIcon } from '@heroicons/react/24/outline'
import PlanGuard from '../../../shared/middleware/planGuard'
import { useAuth } from '../../../shared/components/ProtectedRoute'
import CreateTeamModal from '../components/team/CreateTeamModal'
import InviteTeamMemberModal from '../components/team/InviteTeamMemberModal'
import TeamMembersList from '../components/team/TeamMembersList'
import TeamInvitationList from '../components/team/TeamInvitationList'
import toast from 'react-hot-toast'
import { TeamService } from '../../../shared/services/teamService'
import { useTranslation } from 'react-i18next'

// 팀 관련 타입 정의
interface Team {
  _id: string
  name: string
  description?: string
  ownerId: string
  members: TeamMember[]
  userRole: 'owner' | 'editor' | 'viewer'
  memberCount: number
  createdAt: string
}

interface TeamMember {
  userId: {
    _id: string
    username: string
    email: string
    avatar?: string
  }
  role: 'owner' | 'editor' | 'viewer'
  status: 'active' | 'pending' | 'inactive'
  joinedAt: string
}

const TeamCollaborationPage: React.FC = () => {
  const { t } = useTranslation()
  const { user } = useAuth()
  const [teams, setTeams] = useState<Team[]>([])
  const [loading, setLoading] = useState(true)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showInviteModal, setShowInviteModal] = useState(false)
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null)
  const [showMembersList, setShowMembersList] = useState<string | null>(null)

  useEffect(() => {
    fetchTeams()
  }, [])

  const fetchTeams = async () => {
    try {
      setLoading(true)
      const data = await TeamService.getTeams()

      if (data.success) {
        setTeams((data.teams ?? []) as unknown as Team[])
      } else {
        console.error('팀 조회 API 응답 오류:', data.message)
        toast.error(data.message || t('team.loadError'))
      }
    } catch (error) {
      console.error('팀 조회 실패:', error)
      toast.error(t('team.loadRetry'))
    } finally {
      setLoading(false)
    }
  }

  const handleInviteTeamMember = (team: Team) => {
    setSelectedTeam(team)
    setShowInviteModal(true)
  }

  const handleShowMembers = (teamId: string) => {
    setShowMembersList(showMembersList === teamId ? null : teamId)
  }

  const TeamCard: React.FC<{ team: Team }> = ({ team }) => (
    <div className="rounded-lg border border-gray-200 bg-white shadow-sm transition-shadow hover:shadow-md">
      <div className="p-6">
        <div className="mb-4 flex items-start justify-between">
          <div className="flex items-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-gradient-to-r from-blue-500 to-purple-600">
              <UsersIcon className="h-6 w-6 text-white" />
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {team.name}
              </h3>
              {team.description && (
                <p className="text-sm text-gray-600">{team.description}</p>
              )}
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <span
              className={`rounded-full px-2 py-1 text-xs font-medium ${
                team.userRole === 'owner'
                  ? 'bg-purple-100 text-purple-800'
                  : team.userRole === 'editor'
                    ? 'bg-blue-100 text-blue-800'
                    : 'bg-gray-100 text-gray-800'
              }`}
            >
              {team.userRole === 'owner'
                ? t('team.roleOwner')
                : team.userRole === 'editor'
                  ? t('team.roleEditor')
                  : t('team.roleViewer')}
            </span>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center text-sm text-gray-500">
            <UsersIcon className="mr-1 h-4 w-4" />
            {t('team.memberCount', { count: String(team.memberCount) })}
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => {
                console.log('멤버 보기 클릭:', team._id, team.members)
                handleShowMembers(team._id)
              }}
              className={`rounded-lg px-3 py-1 text-sm font-medium transition-colors ${
                showMembersList === team._id
                  ? 'bg-blue-100 text-blue-800'
                  : 'text-blue-600 hover:bg-blue-50 hover:text-blue-800'
              }`}
            >
              {showMembersList === team._id
                ? t('team.hideMembers')
                : t('team.showMembers')}
            </button>
            <button
              onClick={() => handleInviteTeamMember(team)}
              className="rounded-lg px-3 py-1 text-sm font-medium text-green-600 transition-colors hover:bg-green-50 hover:text-green-800"
            >
              {t('team.invite')}
            </button>
            <button className="rounded p-1 text-sm text-gray-500 hover:bg-gray-100 hover:text-gray-700">
              <CogIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* 팀 멤버 목록 (확장 가능) - 전체 폭 사용 */}
      {showMembersList === team._id && (
        <div className="border-t bg-gray-50">
          {team.members && team.members.length > 0 ? (
            <div className="space-y-6 p-6">
              {/* 현재 팀원 목록 */}
              <TeamMembersList
                teamId={team._id}
                members={team.members}
                userRole={team.userRole}
                onInviteMember={() => handleInviteTeamMember(team)}
                onUpdateMembers={fetchTeams}
              />

              {/* 초대 목록 구분선 */}
              <div className="border-t border-gray-200"></div>

              {/* 보낸 초대 목록 */}
              <TeamInvitationList
                teamId={team._id}
                onInvitationUpdate={fetchTeams}
              />
            </div>
          ) : (
            <div className="py-8 text-center text-gray-500">
              <p>{t('team.noMembers')}</p>
              <button
                onClick={() => handleInviteTeamMember(team)}
                className="mt-2 font-medium text-blue-600 hover:text-blue-800"
              >
                {t('team.inviteFirst')}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )

  const EmptyState = () => (
    <div className="rounded-lg bg-gray-50 py-12 text-center">
      <UsersIcon className="mx-auto h-12 w-12 text-gray-400" />
      <h3 className="mt-4 text-lg font-medium text-gray-900">
        {t('team.noTeams')}
      </h3>
      <p className="mx-auto mt-2 max-w-md text-gray-500">
        {t('team.createHint')}
      </p>
      <button
        onClick={() => setShowCreateModal(true)}
        className="mt-6 transform rounded-lg bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-3 text-white transition-all duration-200 hover:scale-105 hover:from-blue-700 hover:to-purple-700"
      >
        {t('team.createFirst')}
      </button>
    </div>
  )

  return (
    <PlanGuard requiredPlan="growth" redirectTo="/dashboard">
      <div className="min-h-screen bg-gray-50">
        <div className="mx-auto max-w-[95%] px-4 py-8 sm:px-6 lg:px-8 2xl:max-w-7xl">
          {/* 헤더 */}
          <div className="mb-8 flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                {t('team.title')}
              </h1>
              <p className="mt-2 text-gray-600">{t('team.subtitle')}</p>
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex transform items-center rounded-lg bg-gradient-to-r from-blue-600 to-purple-600 px-4 py-2 text-white transition-all duration-200 hover:scale-105 hover:from-blue-700 hover:to-purple-700"
            >
              <PlusIcon className="mr-2 h-5 w-5" />
              {t('team.create')}
            </button>
          </div>

          {/* 플랜 표시 */}
          <div className="mb-6">
            <div className="rounded-lg border border-blue-200 bg-gradient-to-r from-blue-50 to-purple-50 p-4">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-r from-blue-500 to-purple-600">
                    <UsersIcon className="h-5 w-5 text-white" />
                  </div>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-900">
                    {user?.subscription?.plan === 'growth' &&
                      t('team.limitGrowth')}
                    {user?.subscription?.plan === 'scale' &&
                      t('team.limitScale')}
                    {user?.subscription?.plan === 'enterprise' &&
                      t('team.limitEnterprise')}
                  </p>
                  <p className="text-xs text-gray-600">
                    {t('team.currentPlan')}{' '}
                    {user?.subscription?.plan?.toUpperCase() || 'GROWTH'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* 팀 목록 - CSS Grid 개선 */}
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600"></div>
            </div>
          ) : teams.length === 0 ? (
            <EmptyState />
          ) : (
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-1">
              {teams.map(team => (
                <TeamCard key={team._id} team={team} />
              ))}
            </div>
          )}

          {/* 기능 안내 */}
          <div className="mt-12 rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <div className="mb-6 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">
                {t('team.benefitsTitle')}
              </h2>

              {/* 팀 통계 요약 */}
              {teams.length > 0 && (
                <div className="text-sm text-gray-600">
                  총 {teams.length}개 팀 •{' '}
                  {teams.reduce((acc, team) => acc + team.memberCount, 0)}명의
                  {t('team.members')}
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
              <div className="text-center">
                <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-lg bg-blue-100">
                  <UsersIcon className="h-6 w-6 text-blue-600" />
                </div>
                <h3 className="mb-2 font-medium text-gray-900">
                  {t('team.benefitInviteTitle')}
                </h3>
                <p className="text-sm text-gray-600">
                  {t('team.benefitInvite')}
                </p>
              </div>
              <div className="text-center">
                <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-lg bg-green-100">
                  <svg
                    className="h-6 w-6 text-green-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                    />
                  </svg>
                </div>
                <h3 className="mb-2 font-medium text-gray-900">북마크 공유</h3>
                <p className="text-sm text-gray-600">
                  {t('team.benefitShare')}
                </p>
              </div>
              <div className="text-center">
                <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-lg bg-purple-100">
                  <svg
                    className="h-6 w-6 text-purple-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                    />
                  </svg>
                </div>
                <h3 className="mb-2 font-medium text-gray-900">팀 통계</h3>
                <p className="text-sm text-gray-600">
                  {t('team.benefitStats')}
                </p>
              </div>
            </div>

            {/* 빠른 액션 버튼들 */}
            {teams.length > 0 && (
              <div className="mt-8 border-t border-gray-200 pt-6">
                <div className="flex items-center justify-center space-x-4">
                  <button
                    onClick={() => setShowCreateModal(true)}
                    className="inline-flex items-center rounded-lg border border-blue-300 px-4 py-2 text-blue-700 transition-colors hover:bg-blue-50"
                  >
                    <PlusIcon className="mr-2 h-4 w-4" />
                    {t('team.create')}
                  </button>

                  <button
                    onClick={() => {
                      const firstTeam = teams[0]
                      if (firstTeam) {
                        handleInviteTeamMember(firstTeam)
                      }
                    }}
                    className="inline-flex items-center rounded-lg border border-green-300 px-4 py-2 text-green-700 transition-colors hover:bg-green-50"
                  >
                    <svg
                      className="mr-2 h-4 w-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
                      />
                    </svg>
                    {t('team.inviteMemberButton')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* 모달들 */}
        <CreateTeamModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSuccess={fetchTeams}
        />

        {selectedTeam && (
          <InviteTeamMemberModal
            isOpen={showInviteModal}
            onClose={() => {
              setShowInviteModal(false)
              setSelectedTeam(null)
            }}
            onSuccess={fetchTeams}
            teamId={selectedTeam._id}
            teamName={selectedTeam.name}
          />
        )}
      </div>
    </PlanGuard>
  )
}

export default TeamCollaborationPage
